'use client';

import { useMemo, useRef } from 'react';
import { cn } from '@workspace/ui/lib/utils';
import { Icon } from '@/src/components/ui';
import { IBit } from 'types/src/types';
import { BitLockedCard } from '@/src/sections/bits/components/BitLockedCard';
import { BitCard } from '@/src/sections/bits/components/BitCard';

type Props = {
  bits: IBit[];
  className?: string;
  step?: number;
  showArrows?: boolean;
};

export const HorizontalBitsCarousel = ({
  bits,
  className,
  step = 420,
  showArrows = true,
}: Props) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const hasMany = (bits?.length ?? 0) > 1;

  const scrollBy = (dx: number) => {
    const el = scrollerRef.current;
    if (!el) return;

    el.scrollBy({ left: dx, behavior: 'smooth' });
  };

  const containerClasses = useMemo(
    () => cn('relative w-full', className),
    [className]
  );

  return (
    <div className={containerClasses}>
      {showArrows && hasMany && (
        <button
          type="button"
          onClick={() => scrollBy(-step)}
          className={cn(
            'absolute left-1 top-1/2 -translate-y-1/2 z-10 bg-black/30 backdrop-blur border border-black/10 shadow-sm rounded-full p-2 hover:bg-black/50 transition hover:cursor-pointer md:flex hidden'
          )}
          aria-label="Scroll left"
        >
          <Icon name="chevron-left" className="w-5 h-5 text-blue-primary" />
        </button>
      )}

      {showArrows && hasMany && (
        <button
          type="button"
          onClick={() => scrollBy(step)}
          className={cn(
            'absolute right-1 top-1/2 -translate-y-1/2 z-10 bg-black/30 backdrop-blur border border-black/10 shadow-sm rounded-full p-2 hover:bg-black/50 hover:cursor-pointer transition md:flex hidden'
          )}
          aria-label="Scroll right"
        >
          <Icon
            name="chevron-left"
            className="rotate-180 w-5 h-5 text-blue-primary"
          />
        </button>
      )}

      <div
        ref={scrollerRef}
        className={cn(
          'flex gap-1 overflow-x-auto pb-2 scroll-smooth no-scrollbar'
        )}
      >
        {bits.map((bit) => (
          <div key={bit.id} className="shrink-0">
            {!bit.hasAccess ? (
              <BitLockedCard
                authorName={bit.author.name}
                isPartner={bit.author.isPartner}
                authorAvatar={bit.author.avatarUrl}
                createdAt={new Date(bit.createdAt).toISOString()}
                title={bit.title}
                likes={bit.likes.count}
                comments={bit.commentsCount}
                image={bit.coverImageUrl}
                authorLink={bit.author.uniqueLink}
                id={bit.id}
                isBitsExplore={true}
                creatorId={bit.author.id}
                isStripeConnected={bit.isStripeConnected}
                layout="carousel"
              />
            ) : (
              <BitCard
                tags={bit.tags}
                isPartner={bit.author.isPartner}
                authorName={bit.author.name}
                authorAvatar={bit.author.avatarUrl}
                createdAt={new Date(bit.createdAt).toISOString()}
                title={bit.title}
                likes={bit.likes.count}
                comments={bit.commentsCount}
                image={bit.coverImageUrl}
                authorLink={bit.author.uniqueLink}
                id={bit.id}
                isBitsExplore={true}
                slug={bit.slug}
                visibility={bit.visibility}
                layout="carousel"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
